import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

interface MarkdownProps {
  children: string;
  className?: string;
}

/**
 * Minimal markdown renderer styled for the lab notebook. Lab journal
 * entries are dense technical prose with tables, code fences, and inline
 * data snippets — we lean into a mono-ish feel and tight spacing.
 */
export function Markdown({ children, className }: MarkdownProps) {
  return (
    <div className={cn("lab-md text-sm leading-relaxed", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ ...p }) => (
            <h1 className="mb-2 mt-4 text-lg font-semibold" {...p} />
          ),
          h2: ({ ...p }) => (
            <h2 className="mb-2 mt-4 text-base font-semibold" {...p} />
          ),
          h3: ({ ...p }) => (
            <h3 className="mb-1 mt-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground" {...p} />
          ),
          p: ({ ...p }) => <p className="mb-3" {...p} />,
          ul: ({ ...p }) => (
            <ul className="mb-3 ml-5 list-disc space-y-1" {...p} />
          ),
          ol: ({ ...p }) => (
            <ol className="mb-3 ml-5 list-decimal space-y-1" {...p} />
          ),
          li: ({ ...p }) => <li className="pl-1" {...p} />,
          code: ({
            className: cls,
            children,
            ...rest
          }: React.HTMLAttributes<HTMLElement>) => {
            const inline = !/language-/.test(cls ?? "");
            if (inline) {
              return (
                <code
                  className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground"
                  {...rest}
                >
                  {children}
                </code>
              );
            }
            return (
              <code className={cn("font-mono text-xs", cls)} {...rest}>
                {children}
              </code>
            );
          },
          pre: ({ ...p }) => (
            <pre
              className="mb-3 overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs"
              {...p}
            />
          ),
          table: ({ ...p }) => (
            <div className="my-3 overflow-x-auto rounded-md border">
              <table className="w-full text-xs" {...p} />
            </div>
          ),
          th: ({ ...p }) => (
            <th
              className="border-b bg-muted/30 px-3 py-1.5 text-left font-semibold"
              {...p}
            />
          ),
          td: ({ ...p }) => (
            <td className="border-b px-3 py-1.5 align-top" {...p} />
          ),
          blockquote: ({ ...p }) => (
            <blockquote
              className="my-3 border-l-2 border-primary/60 pl-3 italic text-muted-foreground"
              {...p}
            />
          ),
          a: ({ ...p }) => (
            <a
              className="text-primary underline underline-offset-2 hover:text-primary/80"
              target="_blank"
              rel="noreferrer"
              {...p}
            />
          ),
          hr: () => <hr className="my-4 border-border" />,
          strong: ({ ...p }) => (
            <strong className="font-semibold text-foreground" {...p} />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
